/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Display metadata for the audit enums, in one place so the table, the filters, the chips and the
 * detail panel all label a value identically.
 *
 * The lookups are deliberately tolerant: a value the backend adds before the frontend knows about
 * it resolves to a humanised fallback rather than rendering blank. That is the whole point of
 * `metaFor` — a new entity type ships on day one looking deliberate.
 */

import type { ReactNode } from 'react'

import {
  FiBell,
  FiEdit2,
  FiMapPin,
  FiMoreHorizontal,
  FiPlus,
  FiThermometer,
  FiTrash2,
  FiTruck,
  FiUserCheck,
  FiUsers,
} from 'react-icons/fi'
import { RiFileList3Line } from 'react-icons/ri'

import type { Application, ChangeType, EntityType, TimePreset } from './types'
import { CHANGE_TYPES, ENTITY_TYPES } from './types'

/** Turn an unknown enum value into something readable: `alertSubscription` → `Alert subscription`. */
export const humanise = (value: string): string => {
  const words = value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()

  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()
}

export interface ActionMeta {
  label: string
  /** Badge background + text. Colour is never the only signal — the label is always rendered. */
  badgeClass: string
  icon: ReactNode
}

export const ACTION_META: Record<ChangeType, ActionMeta> = {
  create: { label: 'CREATED', badgeClass: 'bg-success text-success-content', icon: <FiPlus /> },
  update: { label: 'UPDATED', badgeClass: 'bg-warning text-warning-content', icon: <FiEdit2 /> },
  delete: { label: 'DELETED', badgeClass: 'bg-error text-error-content', icon: <FiTrash2 /> },
  other: { label: 'OTHER', badgeClass: 'bg-base-300 text-base-content', icon: <FiMoreHorizontal /> },
}

export interface EntityMeta {
  label: string
  icon: ReactNode
}

export const ENTITY_META: Record<EntityType, EntityMeta> = {
  alertConfiguration: { label: 'Alert Configuration', icon: <FiBell /> },
  alertSubscription: { label: 'Alert Subscription', icon: <FiUserCheck /> },
  virtualSetPoint: { label: 'Virtual Setpoint', icon: <FiThermometer /> },
  geofence: { label: 'Geofence', icon: <FiMapPin /> },
  asset: { label: 'Asset', icon: <FiTruck /> },
  userAdministration: { label: 'User Administration', icon: <FiUsers /> },
  other: { label: 'Other', icon: <RiFileList3Line /> },
}

export const APPLICATION_LABELS: Record<Application, string> = {
  refresh: 'Refresh',
  octopus: 'Octopus',
  // `bonito` is the service name; "User Administration" is what the screen calls itself and the
  // only name a reader of this filter would recognise.
  bonito: 'User Administration',
  other: 'Other',
}

/** Metadata for an action, falling back for a value this build does not know. */
export const actionMeta = (changeType: string): ActionMeta =>
  ACTION_META[changeType as ChangeType] ?? {
    label: humanise(changeType).toUpperCase(),
    badgeClass: ACTION_META.other.badgeClass,
    icon: ACTION_META.other.icon,
  }

/** Metadata for an entity type, falling back for a value this build does not know. */
export const entityMeta = (entityType: string): EntityMeta =>
  ENTITY_META[entityType as EntityType] ?? { label: humanise(entityType), icon: ENTITY_META.other.icon }

export const applicationLabel = (application: string): string =>
  APPLICATION_LABELS[application as Application] ?? humanise(application)

export const ACTION_OPTIONS = CHANGE_TYPES
export const ENTITY_OPTIONS = ENTITY_TYPES

export const TIME_PRESETS: { value: Exclude<TimePreset, 'custom'>; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
]

export const TIME_PRESET_LABELS: Record<TimePreset, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  custom: 'Custom range',
}

/** Rows per page. The API caps `take` at 200. */
export const PAGE_SIZE = 50
